import { ReactElement, useCallback, useEffect, useRef } from 'react';
import { IModalProps } from '@components/common/modal/index';
import { useOutsideDialogClose } from '@hooks/useOutsideDialogClose';
import { CloseButton } from '../closeButton';

export const Modal = (props: IModalProps): ReactElement => {
	const { children, className, modalOpen, modalId, onModalClose, isEscAllow = true, modalTitle } = props;
	const dialogRef = useRef<HTMLDialogElement | null>(null);
	useOutsideDialogClose({
		dialogRef,
		callBackFunction: () => props.onModalClose && props.onModalClose(),
	});

	const handleEscapeKey = useCallback(
		(event: KeyboardEvent) => {
			if (event.key === 'Escape' && !isEscAllow) {
				event.preventDefault();
			}
			if (event.key === 'Escape' && isEscAllow) {
				onModalClose && onModalClose();
			}
		},
		[isEscAllow, onModalClose]
	);

	useEffect(() => {
		if (modalOpen) {
			document.addEventListener('keydown', handleEscapeKey);
			return (): void => {
				document.removeEventListener('keydown', handleEscapeKey);
			};
		}
	}, [modalOpen, handleEscapeKey]);

	useEffect(() => {
		const modal = dialogRef?.current;
		const overlay = document.getElementById('overlay') as HTMLElement;

		if (modalOpen) {
			if (modal && !modal.open) {
				modal.showModal();
			}
			document.body.classList.add('overflow-hidden');
			overlay?.classList?.add('show');

			return (): void => {
				if (modal && modal.open) {
					modal.close();
				}
				const openDialogs = document.querySelectorAll('dialog[open]');
				if (openDialogs.length === 0) {
					document.body.classList.remove('overflow-hidden');
					overlay?.classList?.remove('show');
				}
			};
		} else {
			if (modal && modal.open) {
				modal.close();
			}
			const openDialogs = document.querySelectorAll('dialog[open]');
			if (openDialogs.length === 0) {
				document.body.classList.remove('overflow-hidden');
				overlay?.classList?.remove('show');
			}
		}
	}, [modalOpen]);

	return (
		<dialog id={modalId} className={`dialog ${className}`} ref={dialogRef}>
			{modalTitle && <span className="dialog-title">{modalTitle}</span>}
			{onModalClose && <CloseButton onClose={() => onModalClose && onModalClose()} />}
			{children}
		</dialog>
	);
};
